import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useHaptic } from '@/app/hooks/useHaptic';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  block?: boolean;
  loading?: boolean;
  icon?: ReactNode;
  iconRight?: ReactNode;
}

const variants: Record<Variant, string> = {
  primary: 'bg-brand border-brand text-black shadow-raised active:shadow-none',
  secondary: 'bg-surface border-line text-ink shadow-panel',
  ghost: 'bg-transparent border-transparent text-accent shadow-none',
  danger: 'bg-transparent border-transparent text-accent shadow-none',
};

/** Основная кнопка приложения. Тактильный отклик даётся на каждое нажатие. */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', block, loading, icon, iconRight, className, children, onClick, disabled, ...rest },
  ref,
) {
  const haptic = useHaptic();

  return (
    <button
      ref={ref}
      type="button"
      disabled={disabled || loading}
      onClick={(event) => {
        haptic.impact('light');
        onClick?.(event);
      }}
      className={cn(
        'bd-focus inline-flex min-h-[46px] items-center justify-center gap-2 rounded-control border px-4 py-2.5',
        'text-[14px] font-medium leading-tight transition-[transform,opacity,box-shadow] duration-150',
        'active:translate-y-px disabled:pointer-events-none disabled:opacity-50',
        variants[variant],
        block && 'flex w-full',
        className,
      )}
      {...rest}
    >
      {loading ? <Loader2 className="h-4 w-4 animate-bd-spin" aria-hidden /> : icon}
      <span className="truncate">{children}</span>
      {iconRight}
    </button>
  );
});
